/**
 * ============================================================
 * KEJJA OS — src/hooks/useMeals.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  mealsApi,
  type MealType,
  type RecipeCreatePayload, type RecipeUpdatePayload,
  type SlotUpsertPayload,
  type ShoppingCreatePayload, type ShoppingUpdatePayload,
} from '@/lib/mealsApi';

const invalidate = (qc: ReturnType<typeof useQueryClient>, homeId: string) => {
  qc.invalidateQueries({ queryKey: ['meals', homeId] });
};

// ============================================================
// Pulse
// ============================================================

export function useMealsPulse(homeId: string | undefined) {
  return useQuery({
    queryKey: ['meals', homeId, 'pulse'],
    queryFn: () => mealsApi.pulse(homeId!),
    enabled: Boolean(homeId),
    staleTime: 30 * 1000,
  });
}

// ============================================================
// Recipes
// ============================================================

export function useRecipes(homeId: string | undefined, favoritesOnly = false) {
  return useQuery({
    queryKey: ['meals', homeId, 'recipes', { favoritesOnly }],
    queryFn: () => mealsApi.listRecipes(homeId!, favoritesOnly),
    enabled: Boolean(homeId),
  });
}

export function useRecipe(homeId: string | undefined, recipeId: string | undefined) {
  return useQuery({
    queryKey: ['meals', homeId, 'recipes', recipeId],
    queryFn: () => mealsApi.getRecipe(homeId!, recipeId!),
    enabled: Boolean(homeId) && Boolean(recipeId),
  });
}

export function useCreateRecipe(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipeCreatePayload) =>
      mealsApi.createRecipe(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateRecipe(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, patch }: { recipeId: string; patch: RecipeUpdatePayload }) =>
      mealsApi.updateRecipe(homeId!, recipeId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteRecipe(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: string) => mealsApi.deleteRecipe(homeId!, recipeId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useSeedRecipes(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (replace: boolean = false) => mealsApi.seedRecipes(homeId!, replace),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Plan
// ============================================================

export function useMealPlan(homeId: string | undefined, weekStartDate?: string) {
  return useQuery({
    queryKey: ['meals', homeId, 'plan', weekStartDate ?? 'current'],
    queryFn: () => mealsApi.getPlan(homeId!, weekStartDate),
    enabled: Boolean(homeId),
  });
}

export function useUpsertSlot(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SlotUpsertPayload) =>
      mealsApi.upsertSlot(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useClearSlot(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ weekStartDate, dayOfWeek, mealType }: {
      weekStartDate: string; dayOfWeek: number; mealType: MealType;
    }) => mealsApi.clearSlot(homeId!, weekStartDate, dayOfWeek, mealType),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useGenerateList(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekStartDate?: string) => mealsApi.generateList(homeId!, weekStartDate),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Shopping
// ============================================================

export function useShoppingList(homeId: string | undefined, includeBought = false) {
  return useQuery({
    queryKey: ['meals', homeId, 'shopping', { includeBought }],
    queryFn: () => mealsApi.listShopping(homeId!, includeBought),
    enabled: Boolean(homeId),
  });
}

export function useCreateShoppingItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShoppingCreatePayload) =>
      mealsApi.createShopping(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateShoppingItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: ShoppingUpdatePayload }) =>
      mealsApi.updateShopping(homeId!, itemId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useMarkBought(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => mealsApi.markBought(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useMarkPending(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => mealsApi.markPending(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useMarkSkipped(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => mealsApi.markSkipped(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteShoppingItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => mealsApi.deleteShopping(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useClearBought(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => mealsApi.clearBought(homeId!),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}
